import { getCurrentUser } from '@bridebook/toolbox/src/api/auth/getCurrentUser';
import type { PlannerUIMessage } from '@bridebook/web/lib/ai-planner/types';
import { useQuery } from '@tanstack/react-query';

export const plannerHistoryQueryKey = (weddingId: string | undefined) => [
  'ai-planner',
  'history',
  weddingId,
];

export type PlannerHistory = {
  messages: PlannerUIMessage[];
  // Whether the thread was read at all. Empty and unread are different.
  loaded: boolean;
  // When the conversation left onboarding, if it has
  onboardingExitedAt: number | null;
};

export type PlannerHistoryRead = {
  history: PlannerHistory;
  // True until the first read settles (including the wait for a wedding to exist). A degraded
  // read is not pending: retries go on in the background, and the couple gets a usable chat.
  pending: boolean;
};

export const EMPTY_PLANNER_HISTORY: PlannerHistory = {
  messages: [],
  loaded: false,
  onboardingExitedAt: null,
};

// Long enough to spare a struggling store, short enough to pass unnoticed.
const HISTORY_RETRY_MS = 3_000;

const fetchPlannerHistory = async (): Promise<PlannerHistory> => {
  const currentUser = await getCurrentUser();
  const token = currentUser ? await currentUser.getIdToken() : null;
  const response = await fetch('/api/ai-planner/history', {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    return EMPTY_PLANNER_HISTORY;
  }
  const body = (await response.json()) as {
    messages: PlannerUIMessage[];
    readable: boolean;
    onboardingExitedAt?: number | null;
  };
  return {
    messages: body.messages ?? [],
    loaded: Boolean(body.readable),
    onboardingExitedAt: body.onboardingExitedAt ?? null,
  };
};

// What a clear discarded, beyond the thread itself.
export type PlannerClearOptions = {
  // Facts, deferrals, the offer log and the ask history.
  loop?: boolean;
  // What the coach worked out about the couple, injected into every prompt.
  memory?: boolean;
  // The wedding itself, back to what signup writes
  wedding?: boolean;
  // A test phone number on the user, so an approved enquiry sends; false removes their number
  phone?: boolean;
};

export type PlannerClearResult = {
  cleared: number;
  readable: boolean;
  loop?: boolean;
  memory?: boolean;
  wedding?: boolean;
  phone?: boolean;
};

/**
 * Discards the stored thread, and whatever else the caller asks for. Off in production.
 * The browser still holds the messages and the next turn posts them back as history, so the
 * caller must empty the chat list too.
 */
export const clearPlannerHistory = async ({
  loop = false,
  memory = false,
  wedding = false,
  phone,
}: PlannerClearOptions = {}): Promise<PlannerClearResult> => {
  const currentUser = await getCurrentUser();
  const token = currentUser ? await currentUser.getIdToken() : null;
  const query = new URLSearchParams({
    ...(loop ? { loop: '1' } : {}),
    ...(memory ? { memory: '1' } : {}),
    ...(wedding ? { wedding: '1' } : {}),
    ...(phone === undefined ? {} : { phone: phone ? '1' : '0' }),
  }).toString();
  const response = await fetch(`/api/ai-planner/history${query ? `?${query}` : ''}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) return { cleared: 0, readable: false };
  return (await response.json()) as PlannerClearResult;
};

/**
 * The wedding's conversation so far, read once when the planner opens. Fetched rather
 * than rendered into the page, because `useChat` owns the message list and this seeds it.
 * A window over the thread, not the thread: the server applies the model's own window.
 */
export const usePlannerHistory = (weddingId: string | undefined): PlannerHistoryRead => {
  const { data, isLoading } = useQuery(plannerHistoryQueryKey(weddingId), fetchPlannerHistory, {
    enabled: Boolean(weddingId),
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
    // An unreadable thread answers with a 200, so `retry` never sees a failure to
    // act on. Without this, one hiccup as the planner opens leaves the couple on a
    // blank conversation for the whole visit. The loop stops as soon as a read
    // comes back trustworthy.
    refetchInterval: (current) => (current?.loaded ? false : HISTORY_RETRY_MS),
  });

  return { history: data ?? EMPTY_PLANNER_HISTORY, pending: isLoading };
};
